// The registration form asks "is this one of these people?" as it is filled in. This decides when
// there is enough on the form to ask, and what to send. It mirrors PatientLookalikeService on the
// backend: a complete identifier, or a name with something to pin it to. Anything less is a round
// trip that can only come back empty.
//
// Half-typed identifiers are deliberately left out of the request rather than sent as they stand:
// twelve digits of a CNIC would match on the stem and read as a typo of someone else's.

const CNIC = /^\d{5}-\d{7}-\d$/;
const digits = (s) => String(s || '').replace(/\D/g, '');
const blank = (s) => !String(s || '').trim();
const trimmed = (s) => String(s || '').trim();

/**
 * The look-alike request for the form as it stands, or null when there is not yet enough to ask.
 *
 * The father's name used to be read out of the guardian's name when the guardian was the father,
 * mirroring a derivation the save made. Since 2026-09-20 the form asks for both parents outright,
 * so it is simply the field.
 *
 * `isMinor` decides which of the fields only one kind of patient has are read. The form keeps a
 * field the date of birth hides (so a date typed through a wrong age does not wipe it), and a hidden
 * value must not be matched on: an adult's CNIC left on a child's form would match that adult as a
 * sure repeat, and a child's B-Form or parent CNIC left on an adult's form would match the child.
 */
export const lookalikeQueryFrom = (form, isMinor) => {
  const fatherName = trimmed(form.fatherName);
  const cnic = isMinor ? '' : trimmed(form.cnic);
  const husbandName = isMinor ? '' : trimmed(form.husbandName);
  // A child's identifying CNIC is a PARENT's now. The guardian's is still read for files written
  // before that, and because a non-parent guardian's CNIC is still worth matching on.
  const parentCnic = isMinor ? trimmed(form.fatherCnic) || trimmed(form.motherCnic) : '';
  const guardianCnic = isMinor ? parentCnic || trimmed(form.guardianCnic) : '';
  const bFormNumber = isMinor ? trimmed(form.bFormNumber) : '';
  const phone = trimmed(form.phoneNumber);

  const named = !blank(form.firstName) && !blank(form.lastName);
  const hasPhone = digits(phone).length >= 10;
  const hasDob = !blank(form.dateOfBirth);
  const hasParent = !!fatherName || !!husbandName;

  const enough =
    CNIC.test(cnic)
    || CNIC.test(guardianCnic)
    || CNIC.test(bFormNumber)
    || (named && (hasParent || hasDob || hasPhone))
    || (hasPhone && hasDob);
  if (!enough) return null;

  return {
    firstName: trimmed(form.firstName) || null,
    lastName: trimmed(form.lastName) || null,
    fatherName: fatherName || null,
    husbandName: husbandName || null,
    phoneNumber: hasPhone ? phone : null,
    cnic: CNIC.test(cnic) ? cnic : null,
    guardianCnic: CNIC.test(guardianCnic) ? guardianCnic : null,
    bFormNumber: CNIC.test(bFormNumber) ? bFormNumber : null,
    dateOfBirth: hasDob ? form.dateOfBirth : null,
    // A date derived from an age is matched as a year-wide band, not a day.
    dobEstimated: hasDob ? !!form.dobEstimated : null,
  };
};

/** A sure or likely match holds Register until the receptionist says this is not one of them. */
export const needsAcknowledgement = (lookalikes) =>
  (lookalikes || []).some((c) => c.strength === 'CERTAIN' || c.strength === 'LIKELY');

/** The repeated-identifier match, if there is one: it is named at the top of the panel. */
export const certainOf = (lookalikes) =>
  (lookalikes || []).find((c) => c.strength === 'CERTAIN') || null;

/**
 * The patient already holding the CNIC typed, if the answer found one. Unlike any other match this
 * cannot be waved through: the server refuses a second patient with the same CNIC, so "It's a
 * different person" would only switch Register on for a save that is certain to fail. The form
 * holds Register until the number is corrected or that file is opened instead.
 */
export const cnicTakenBy = (lookalikes) =>
  (lookalikes || []).find((c) => c.strength === 'CERTAIN' && (c.rules || []).includes('SAME_CNIC')) || null;
